import { useId, useMemo, useState } from 'react';
import type { ClarifyQuestion } from './parseWorkflowSteps';

type ClarifyBoxProps = {
  questions: ClarifyQuestion[];
  disabled?: boolean;
  onSubmit?: (answer: string) => void;
};

function formatClarifyAnswer(
  questions: ClarifyQuestion[],
  selections: Record<number, string[]>,
  notes: string,
): string {
  const lines = questions
    .map((item, index) => {
      const chosen = selections[index] ?? [];
      if (chosen.length === 0) return null;
      if (questions.length === 1) return chosen.join(', ');
      return `${item.question}: ${chosen.join(', ')}`;
    })
    .filter((line): line is string => Boolean(line));

  const trimmedNotes = notes.trim();
  if (trimmedNotes) {
    lines.push(`Further specifications: ${trimmedNotes}`);
  }

  return lines.join('\n');
}

export default function ClarifyBox({ questions, disabled = false, onSubmit }: ClarifyBoxProps) {
  const instanceId = useId();
  const [selections, setSelections] = useState<Record<number, string[]>>({});
  const [notes, setNotes] = useState('');

  const allAnswered = useMemo(
    () => questions.every((_, index) => (selections[index]?.length ?? 0) > 0),
    [questions, selections],
  );

  if (questions.length === 0) return null;

  const selectSingle = (questionIndex: number, option: string) => {
    setSelections(current => ({ ...current, [questionIndex]: [option] }));
  };

  const toggleMulti = (questionIndex: number, option: string) => {
    setSelections(current => {
      const prev = current[questionIndex] ?? [];
      const next = prev.includes(option) ? prev.filter(value => value !== option) : [...prev, option];
      return { ...current, [questionIndex]: next };
    });
  };

  const handleSubmit = () => {
    const answer = formatClarifyAnswer(questions, selections, notes);
    if (!answer) return;
    onSubmit?.(answer);
  };

  return (
    <div className="aida-clarify" data-testid="aida-clarify-box" data-clarify-id={instanceId}>
      {questions.map((item, questionIndex) => {
        const selected = selections[questionIndex] ?? [];
        const groupName = `${instanceId}-q${questionIndex}`;
        const labelId = `${groupName}-label`;

        return (
          <section
            key={`${item.question}-${questionIndex}`}
            className="aida-clarify__question"
            aria-labelledby={labelId}
          >
            <h3 id={labelId} className="aida-clarify__prompt">
              {item.question}
            </h3>
            <div
              className="aida-clarify__options"
              role={item.multi ? 'group' : 'radiogroup'}
              aria-labelledby={labelId}
            >
              {item.options.map((option, optionIndex) => {
                const isSelected = selected.includes(option);
                const optionId = `${groupName}-o${optionIndex}`;

                return (
                  <label
                    key={`${optionIndex}-${option}`}
                    htmlFor={optionId}
                    className={`aida-clarify__option${isSelected ? ' aida-clarify__option--selected' : ''}`}
                  >
                    <input
                      id={optionId}
                      className="aida-clarify__input"
                      type={item.multi ? 'checkbox' : 'radio'}
                      name={item.multi ? optionId : groupName}
                      checked={isSelected}
                      disabled={disabled || !onSubmit}
                      onChange={() =>
                        item.multi ? toggleMulti(questionIndex, option) : selectSingle(questionIndex, option)
                      }
                    />
                    <span
                      className={`aida-clarify__control${
                        item.multi ? ' aida-clarify__control--checkbox' : ' aida-clarify__control--radio'
                      }`}
                      aria-hidden="true"
                    />
                    <span className="aida-clarify__option-label">{option}</span>
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}

      <label className="aida-clarify__notes" htmlFor={`${instanceId}-notes`}>
        <span className="aida-clarify__notes-label">Add any further specifications</span>
        <span className="aida-clarify__notes-optional">Optional</span>
        <textarea
          id={`${instanceId}-notes`}
          className="aida-clarify__notes-input"
          rows={2}
          value={notes}
          disabled={disabled || !onSubmit}
          placeholder="Extra context, constraints, or preferred output…"
          onChange={event => setNotes(event.target.value)}
        />
      </label>

      <button
        type="button"
        className="aida-clarify__submit"
        disabled={disabled || !onSubmit || !allAnswered}
        onClick={handleSubmit}
      >
        Continue
      </button>
    </div>
  );
}

export function ClarifyBoxLoader() {
  return (
    <div className="aida-stream-loader" data-testid="aida-clarify-loader" role="status" aria-live="polite">
      <div className="aida-stream-loader__skeleton" aria-hidden="true">
        <span className="aida-stream-loader__line aida-stream-loader__line--title" />
        <span className="aida-stream-loader__line" />
        <span className="aida-stream-loader__line" />
        <span className="aida-stream-loader__line aida-stream-loader__line--short" />
      </div>
      <div className="aida-thinking-loader" aria-label="Preparing clarification options">
        <span className="aida-dot" />
        <span className="aida-dot" />
        <span className="aida-dot" />
      </div>
    </div>
  );
}
